import useTypedContext from "hooks/useTypedContext";
import useAnalytics from "hooks/useAnalytics";
import { getManagementStrategy } from "views/Account/Settings/helpers";
import { SettingsContext } from "views/Account/Settings/Context";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import FlashContext from "components/FlashMessages/FlashContext";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useDeleteGroup from "views/Account/Settings/IdpGroup/useDeleteGroup";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useCanManageIdpGroups from "shared/IdpGroup/access/useCanManageIdpGroups";

import { showIdpGroupDeleteConfirmationModal } from "../../../IdpGroup/ConfirmationModal";
import { showAssignRoleDrawer } from "../../../IdpGroup/AssignRoleDrawer";
import { showIdpGroupMappingEditDrawer } from "../../EditDetailsDrawer";
import { ManagedUserGroupView } from "../../types";

const IdpGroupMappingTableRowActionsCell = ({
  item: group,
  ...cellProps
}: TableSimpleCellProps<ManagedUserGroupView>) => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const { onDelete, loading } = useDeleteGroup(["GetManagedUserGroupsV2"]);
  const canManageIdpGroups = useCanManageIdpGroups();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationIdpGroupMapping,
    defaultCallbackTrackProperties: { managementStrategy: getManagementStrategy(activationStatus) },
  });

  const handleRemoveGroup = () => {
    trackSegmentAnalyticsEvent("Delete confirm");
    onDelete(group.id)
      .then(({ data }) => {
        if (data) {
          reportSuccess({
            message: `IdP group mapping was deleted`,
          });
        }
      })
      .catch(onError);
  };

  const getOnDeleteClick = () => {
    showIdpGroupDeleteConfirmationModal({
      onConfirm: handleRemoveGroup,
      name: group.groupName,
    });
    trackSegmentAnalyticsEvent("Delete group click");
  };

  const handleEditDetailsClick = () => {
    trackSegmentAnalyticsEvent("Open edit details drawer");
    showIdpGroupMappingEditDrawer({ currentGroup: group, activationStatus });
  };

  const handleManageRolesClick = () => {
    trackSegmentAnalyticsEvent("Open manage roles drawer");
    showAssignRoleDrawer({ userGroupId: group.id, activationStatus });
  };

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Group actions">
        <DropdownMenuItem onAction={handleManageRolesClick}>Manage roles</DropdownMenuItem>
        {canManageIdpGroups && (
          <DropdownMenuItem onAction={handleEditDetailsClick}>Edit details</DropdownMenuItem>
        )}
        {canManageIdpGroups && (
          <DropdownMenuItem loading={loading} danger onAction={getOnDeleteClick}>
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default IdpGroupMappingTableRowActionsCell;
